import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FileItem, StorageQuota } from '@authority-labs/api-interfaces';
import { AuthenticationService } from '@check-list/app/auth/authentication.service';
import { UploadFile } from '@check-list/core/models/upload-file.model';
import { environment } from '@check-list/environments/environment';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable, firstValueFrom } from 'rxjs';

interface UploadResponseItem {
  id?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FilesService {
  fileList: UploadFile[] = [];
  showUploadQueue = false;

  private baseUrl = `${environment.API}/files`;
  private downloadUrl = `${this.baseUrl}/download`;

  failed = 0;
  successfully = 0;
  cancelled = 0;
  entityInfo: { name: string; id: string };

  constructor(
    private http: HttpClient,
    private msg: NzMessageService,
    private authService: AuthenticationService
  ) {}

  async handleUpload(file: UploadFile): Promise<void> {
    try {
      const parentId = file.uploadZone.id;
      const fileName = file.file.name;

      // Step 1: Create upload session
      const session = await firstValueFrom(
        this.createUploadSession(parentId, fileName, file.file.type)
      );

      if (!session?.uploadUrl) {
        throw new Error('Failed to create upload session');
      }

      // Step 2: Upload file using the temporary URL
      const fileBlob = file.file as unknown as File;
      const fileSize = fileBlob.size;

      file.file.status = 'uploading';

      let uploadedItem: UploadResponseItem;
      // For files larger than 4MB, use chunked upload
      if (fileSize > 4 * 1024 * 1024) {
        uploadedItem = await this.uploadLargeFileToSession(
          session.uploadUrl,
          fileBlob,
          file
        );
      } else {
        uploadedItem = await this.uploadSmallFileToSession(
          session.uploadUrl,
          fileBlob,
          file
        );
      }

      // Step 3: Resolve file ID - from upload response or fallback to API lookup
      let fileId = uploadedItem?.id;
      if (!fileId) {
        const resolved = await firstValueFrom(
          this.resolveFile(parentId, fileName)
        );
        fileId = resolved?.id;
      }

      file.file.response = { id: fileId };
      file.file.status = 'done';
      file.isReady = false;
      this.handleChange(file);
    } catch (error) {
      this.msg.error(`${file.file.name} העלאת הקובץ נכשלה.`);
      file.message = 'error';
      file.file.status = 'error';
      file.isReady = false;
      file.done();
    }
  }

  private async uploadSmallFileToSession(
    uploadUrl: string,
    fileBlob: File,
    uploadFile: UploadFile
  ): Promise<UploadResponseItem> {
    const fileSize = fileBlob.size;

    const response = await firstValueFrom(
      this.http.put<UploadResponseItem>(uploadUrl, fileBlob, {
        headers: {
          'Content-Range': `bytes 0-${fileSize - 1}/${fileSize}`
        },
        observe: 'response'
      })
    );

    uploadFile.file.percent = 100;
    return response.body;
  }

  private async uploadLargeFileToSession(
    uploadUrl: string,
    fileBlob: File,
    uploadFile: UploadFile
  ): Promise<UploadResponseItem> {
    const chunkSize = 10 * 1024 * 1024; // 10MB chunks
    const totalSize = fileBlob.size;
    let offset = 0;
    let lastBody: UploadResponseItem = null;

    while (offset < totalSize) {
      const chunkLength = Math.min(chunkSize, totalSize - offset);
      const chunk = fileBlob.slice(offset, offset + chunkLength);
      const rangeEnd = offset + chunkLength - 1;

      const response = await firstValueFrom(
        this.http.put<UploadResponseItem>(uploadUrl, chunk, {
          headers: {
            'Content-Range': `bytes ${offset}-${rangeEnd}/${totalSize}`
          },
          observe: 'response'
        })
      );

      if (response.status === 202) {
        // Chunk uploaded successfully, more to come
        offset += chunkLength;
        uploadFile.file.percent = Math.round((offset / totalSize) * 100);
        continue;
      }

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`Upload failed: ${response.status}`);
      }

      // Upload complete
      lastBody = response.body;
      offset += chunkLength;
      uploadFile.file.percent = 100;
    }

    return lastBody;
  }

  getFiles(directoryId: string): Observable<FileItem[]> {
    return this.http.get<FileItem[]>(`${this.baseUrl}/${directoryId}`);
  }

  get isAllDone(): boolean {
    return this.successfully === this.fileList.length - this.cancelled;
  }

  get inProgress(): number {
    return (
      this.fileList.length - this.cancelled - (this.failed + this.successfully)
    );
  }

  handleChange(file: UploadFile): void {
    const status = file.file.status;
    if (status === 'done') {
      this.msg.success(`${file.file.name} הקובץ הועלה בהצלחה.`);
      this.successfully += 1;
    } else if (status === 'error') {
      this.msg.error(`${file.file.name} העלאת הקובץ נכשלה.`);
      file.message = 'error';
      this.failed += 1;
    }
    file.done();
  }

  clearQueue() {
    this.fileList = this.fileList.filter((i) => !!i.isReady);
    this.failed = 0;
    this.successfully = 0;
    this.cancelled = 0;
  }

  upload(uploadList: UploadFile[]) {
    this.fileList = [...uploadList, ...this.fileList];
    uploadList.forEach((i) => this.handleUpload(i));
  }

  cancelItem(item: UploadFile) {
    item.cancelUpload();
    this.cancelled += 1;
  }

  reUpload(uploadFile: UploadFile) {
    if (uploadFile.isCancelled) {
      this.cancelled -= 1;
    }
    uploadFile.upload();
    this.handleUpload(uploadFile);
  }

  async downloadFile({
    fileId,
    url,
    openInNewTab = false
  }: {
    fileId?: string;
    url?: string;
    openInNewTab?: boolean;
  }): Promise<void> {
    if (!fileId && !url) {
      return;
    }

    try {
      let targetUrl: string;

      if (fileId) {
        // SharePoint file - get pre-authenticated download URL from API
        const { downloadUrl } = await firstValueFrom(
          this.http.get<{
            downloadUrl: string;
            fileName: string;
            mimeType: string;
          }>(`${this.downloadUrl}/${fileId}`)
        );
        targetUrl = downloadUrl;
      } else {
        // Direct URL (e.g., inspection export) - fetch as blob
        const token = this.authService.getAuthToken();
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const blob = await response.blob();
        targetUrl = window.URL.createObjectURL(blob);
      }

      if (openInNewTab) {
        window.open(targetUrl, '_blank');
      } else {
        window.location.href = targetUrl;
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  }

  storageDetails(): Observable<StorageQuota> {
    return this.http.get<StorageQuota>(`${this.baseUrl}/drive/status`);
  }

  getWebUrl(
    fileId: string,
    path?: string
  ): Observable<{ sharepointId: string; webUrl: string }> {
    const params = path ? { path } : {};
    return this.http.get<{ sharepointId: string; webUrl: string }>(
      `${this.baseUrl}/weburl/${fileId}`,
      { params }
    );
  }

  createUploadSession(
    parentId: string,
    fileName: string,
    mimeType: string
  ): Observable<{ uploadUrl: string; expirationDateTime: string }> {
    return this.http.post<{ uploadUrl: string; expirationDateTime: string }>(
      `${this.baseUrl}/upload-session`,
      { parentId, fileName, mimeType }
    );
  }

  resolveFile(parentId: string, fileName: string): Observable<FileItem> {
    return this.http.get<FileItem>(
      `${this.baseUrl}/resolve/${encodeURIComponent(
        parentId
      )}/${encodeURIComponent(fileName)}`
    );
  }
}
